// Removed duplicate/invalid class definition. All logic is now inside the main class below.
import {
  CandidateProfile,
  CvProject,
  FocusArea,
  CvGenerationRequest,
  CvGenerationResponse,
  generateCv,
} from '../../services/cvGenerator';
import {
  CandidateProfileDto,
  CvProjectDto,
} from './cvApiDtos';
import {
  mapCandidateProfileDtoToDomain,
  mapCvProjectDtoToDomain,
  mapGenerateCvRequestDtoToDomain,
  mapGenerateCvRequestToDto,
} from './cvApiMappers';
import { ApiLanguage, CvDataCollection } from './cvDataCollection';
import PLACEHOLDER_CANDIDATE_PROFILE_EN_DTO from './data/profile.en.json';
import PLACEHOLDER_CANDIDATE_PROFILE_NL_DTO from './data/profile.nl.json';
import PLACEHOLDER_PROJECT_TEMPLATES_EN_DTO from './data/projects.en.json';
import PLACEHOLDER_PROJECT_TEMPLATES_NL_DTO from './data/projects.nl.json';

const candidateProfileByLanguage: Record<ApiLanguage, CandidateProfileDto> = {
  en: PLACEHOLDER_CANDIDATE_PROFILE_EN_DTO as CandidateProfileDto,
  nl: PLACEHOLDER_CANDIDATE_PROFILE_NL_DTO as CandidateProfileDto,
};

const projectTemplatesByLanguage: Record<ApiLanguage, CvProjectDto[]> = {
  en: PLACEHOLDER_PROJECT_TEMPLATES_EN_DTO as CvProjectDto[],
  nl: PLACEHOLDER_PROJECT_TEMPLATES_NL_DTO as CvProjectDto[],
};

const unique = (items: string[]): string[] => Array.from(new Set(items));

const toDomainProjects = (language: ApiLanguage): CvProject[] =>
  projectTemplatesByLanguage[language].map(mapCvProjectDtoToDomain);

export class PlaceholderCvDataCollection implements CvDataCollection {
  async getCandidateProfile(language: ApiLanguage): Promise<CandidateProfile> {
    return Promise.resolve(
      mapCandidateProfileDtoToDomain(candidateProfileByLanguage[language])
    );
  }

  async getAvailableFocusAreas(language: ApiLanguage): Promise<FocusArea[]> {
    const projectTemplatesDto = projectTemplatesByLanguage[language];

    return Promise.resolve(
      unique(
        projectTemplatesDto.flatMap((project) => project.focusAreas)
      )
    );
  }

  async getAvailableTechnologies(language: ApiLanguage): Promise<string[]> {
    const projectTemplatesDto = projectTemplatesByLanguage[language];

    return Promise.resolve(
      unique(
        projectTemplatesDto.flatMap((project) => project.technologies)
      ).sort()
    );
  }

  async generateCv(
    request: CvGenerationRequest,
    language: ApiLanguage
  ): Promise<CvGenerationResponse> {
    const requestDto = mapGenerateCvRequestToDto(request);
    const domainRequest = mapGenerateCvRequestDtoToDomain(requestDto);
    const candidateProfileDto = candidateProfileByLanguage[language];
    const profile = mapCandidateProfileDtoToDomain(candidateProfileDto);
    const response = generateCv(
      domainRequest,
      profile,
      toDomainProjects(language)
    );
    const profileDescription = profile.description?.trim();

    if (profileDescription) {
      return Promise.resolve({
        ...response,
        profile,
        summary: profileDescription,
      });
    }

    return Promise.resolve(response);
  }
  async getEarliestProjectDate(language: ApiLanguage): Promise<string> {
    const projects = toDomainProjects(language);
    // Only consider projects with a valid completedAt date
    const validDates = projects
      .map((project) => project.completedAt)
      .filter((dateStr) => !!dateStr && !isNaN(Date.parse(dateStr)));
    if (validDates.length > 0) {
      const dates = validDates.map((dateStr) => new Date(dateStr));
      const earliestDate = new Date(Math.min(...dates.map((d) => d.getTime())));
      return earliestDate.toISOString().slice(0, 10);
    }
    // Fallback to 3 years ago if no valid project dates
    const date = new Date();
    date.setFullYear(date.getFullYear() - 3);
    return date.toISOString().slice(0, 10);
  }
}
